import { isPWA } from '@wix/da-shared-react/pkg/utils/isPWA';

type Callback = (payload: any) => void;

interface ListenerItem {
  type: string;
  callback: Callback;
}

interface TabsManagerConfig {
  channelName?: string;
  onBecomePrimary?: (isPrimary: boolean) => void;
}

export class TabsManager {
  private readonly tabId: string;
  private channelName: string;
  private channel: BroadcastChannel | null = null;
  private listeners: ListenerItem[] = [];
  private onBecomePrimaryCallback?: (isPrimary: boolean) => void;
  private readonly isPwa: boolean;

  // Primary tab tracking
  public isPrimary: boolean = false;

  // Constants (computed based on isPwa)
  private get TAB_PREFIX(): string {
    return this.isPwa ? 'da_tab_pwa_id_' : 'da_tab_id_';
  }

  private get PRIMARY_TAB_ID(): string {
    return this.isPwa ? 'da_tab_pwa_primary_id' : 'da_tab_primary_id';
  }

  private get PRIMARY_STATUS(): string {
    return this.isPwa ? 'da_tab_pwa_primary' : 'da_tab_primary';
  }

  private get SLAVE_STATUS(): string {
    return this.isPwa ? 'da_tab_pwa_secondary' : 'da_tab_secondary';
  }

  private get STATUS_CHANGED_EVENT(): string {
    return this.isPwa ? 'PWA_DA_TAB_STATUS_CHANGED' : 'DA_TAB_STATUS_CHANGED';
  }

  // Singleton instance
  private static instance: TabsManager | null = null;

  constructor(config?: TabsManagerConfig) {
    // Return existing instance if it exists
    if (TabsManager.instance) {
      return TabsManager.instance;
    }

    // Initialize isPwa before using constants
    this.isPwa = isPWA();
    this.tabId = this.TAB_PREFIX + Date.now().toString();
    this.channelName = config?.channelName || 'da_tab_channel';
    this.onBecomePrimaryCallback = config?.onBecomePrimary;

    this.init();

    // Store instance
    TabsManager.instance = this;
  }

  /**
   * Initialize tab management and broadcast channel
   */
  private init() {
    if (typeof window === 'undefined') return;

    // Initialize broadcast channel
    this.channel = new BroadcastChannel(this.channelName);
    this.channel.onmessage = this.handleMessage.bind(this);
    this.channel.onmessageerror = this.handleError.bind(this);

    // Set up tab status management
    this.initTabStatus();
    this.setupEventListeners();
  }

  /**
   * Initialize this tab's primary/secondary status
   */
  private initTabStatus() {
    // Validate existing primary tab before proceeding
    if (!this.validatePrimaryTab()) {
      this.recoverPrimaryTab();
    }

    if (!localStorage.getItem(this.PRIMARY_TAB_ID)) {
      this.setPrimaryTab(this.tabId);
    } else {
      this.setSecondaryTab(this.tabId);
    }
    this.notifyTabStatus();
  }

  /**
   * Set up window event listeners
   */
  private setupEventListeners() {
    // Listen for beforeunload to handle tab closure
    window.addEventListener('pagehide', () => {
      if (this.checkIsPrimaryTab()) {
        localStorage.removeItem(this.PRIMARY_TAB_ID);
        this.transferPrimaryStatus();
      }
      localStorage.removeItem(this.tabId);
    });

    // Listen for storage changes (primary tab changes)
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === this.PRIMARY_TAB_ID) {
        this.notifyTabStatus();
      }
    });

    // Listen for primary status changes
    window.addEventListener(
      this.STATUS_CHANGED_EVENT,
      ((event: CustomEvent) => {
        const wasPrimary = this.isPrimary;
        this.isPrimary = this.checkIsPrimaryTab();

        // Notify callback when becoming primary
        if (!wasPrimary && this.isPrimary && this.onBecomePrimaryCallback) {
          this.onBecomePrimaryCallback(this.isPrimary);
        }
      }) as EventListener,
      { passive: true }
    );
  }

  /**
   * Set a tab as primary
   */
  private setPrimaryTab(id: string) {
    localStorage.setItem(this.PRIMARY_TAB_ID, id);
    localStorage.setItem(id, this.PRIMARY_STATUS);
  }

  /**
   * Set a tab as secondary
   */
  private setSecondaryTab(id: string) {
    localStorage.setItem(id, this.SLAVE_STATUS);
  }

  /**
   * Check if current tab is primary
   */
  private checkIsPrimaryTab(): boolean {
    return localStorage.getItem(this.PRIMARY_TAB_ID) === this.tabId;
  }

  /**
   * Validate that PRIMARY_TAB_ID points to an existing tab entry
   */
  private validatePrimaryTab(): boolean {
    const primaryTabId = localStorage.getItem(this.PRIMARY_TAB_ID);
    if (!primaryTabId) {
      return true; // No primary tab set, which is valid
    }
    // Check if the tab entry exists
    const tabStatus = localStorage.getItem(primaryTabId);
    return tabStatus !== null;
  }

  /**
   * Get all keys from localStorage
   */
  private getLocalStorageKeys(): string[] {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key !== null) {
        keys.push(key);
      }
    }
    return keys;
  }

  /**
   * Recover from orphaned PRIMARY_TAB_ID by promoting a secondary tab or cleaning up
   */
  private recoverPrimaryTab() {
    // Remove the orphaned PRIMARY_TAB_ID first
    localStorage.removeItem(this.PRIMARY_TAB_ID);

    // Use same logic as transferPrimaryStatus
    const tabs = this.getLocalStorageKeys().filter(
      key => key !== this.PRIMARY_TAB_ID && localStorage.getItem(key) === this.SLAVE_STATUS
    );

    if (tabs.length > 0) {
      this.setPrimaryTab(tabs[0]);
    }

    this.notifyTabStatus();
  }

  /**
   * Transfer primary status to another tab
   */
  private transferPrimaryStatus() {
    const tabs = this.getLocalStorageKeys().filter(
      key =>
        key !== this.PRIMARY_TAB_ID &&
        key !== this.tabId &&
        localStorage.getItem(key) === this.SLAVE_STATUS
    );

    if (tabs.length > 0) {
      this.setPrimaryTab(tabs[0]);
    } else {
      localStorage.removeItem(this.PRIMARY_TAB_ID);
    }
  }

  /**
   * Notify tabs of status change
   */
  private notifyTabStatus() {
    if (typeof window === 'undefined') return;

    this.isPrimary = this.checkIsPrimaryTab();

    window.dispatchEvent(
      new CustomEvent(this.STATUS_CHANGED_EVENT, {
        detail: {
          tabId: this.tabId,
          isPrimary: this.isPrimary,
        },
      })
    );
  }

  /**
   * Handle incoming broadcast messages
   */
  private handleMessage(event: MessageEvent) {
    const { type, payload } = event.data;

    this.listeners = this.listeners.filter(item => {
      if (item.type === type || item.type === '*') {
        try {
          item.callback({ type, payload });
        } catch (e) {
          // Silently handle callback errors
        }
      }
      return true;
    });
  }

  /**
   * Handle broadcast channel errors
   */
  private handleError(error: MessageEvent) {
    // Silently handle broadcast channel errors
  }

  /**
   * Register a listener for a specific event type
   * @param type - Event type to listen for (or '*' for all events)
   * @param callback - Callback function to execute when event is received
   */
  on(type: string, callback: Callback) {
    this.listeners.push({ type, callback });
  }

  /**
   * Unregister listeners for a specific event type
   * @param type - Event type to unregister
   */
  off(type: string) {
    this.listeners = this.listeners.filter(item => item.type !== type);
  }

  /**
   * Emit an event to all tabs
   * @param type - Event type
   * @param payload - Data to send with the event
   */
  emit(type: string, payload?: any) {
    if (!this.channel) return;

    const message = { type, payload };
    this.channel.postMessage(message);
  }

  /**
   * Get the current tab ID
   */
  getTabId(): string {
    return this.tabId;
  }

  /**
   * Clean up resources
   */
  destroy() {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    this.listeners = [];
    TabsManager.instance = null;
  }
}
